import { BLOG_STATUS_DRAFT, BLOG_STATUS_PUBLISHED, type BlogStatus } from '@iws/theme-schema/blog'
import { AlertCircle, ArchiveRestore, Eye, FileText, Pencil, RefreshCw, Search, Trash2 } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import { useState } from 'react'

import { getFileUrl } from '@/clients/http'
import { useBlogPosts, useDeleteBlogPost, usePermanentDeleteBlogPost, useRestoreBlogPost } from '@/hooks/features/blog'
import { useDebouncedCallback } from '@/hooks/useDebounce'

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Pagination } from '@/components/ui/pagination-controls'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'

import { ConfirmDialog, PageHeader } from '@/components/atoms'

import type { BlogPost } from '@/types/blog'

import { BlogStatusBadge } from './BlogStatusBadge'

type StatusTab = BlogStatus | 'all' | 'trash'

const TABS: { value: StatusTab; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: BLOG_STATUS_PUBLISHED, label: 'Published' },
  { value: BLOG_STATUS_DRAFT, label: 'Drafts' },
  { value: 'trash', label: 'Trash' },
]

const formatDate = (iso: string) => new Date(iso).toLocaleDateString(undefined, { dateStyle: 'medium' })

export const BlogList = () => {
  const navigate = useNavigate()
  const [status, setStatus] = useState<StatusTab>('all')
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [length, setLength] = useState(10)
  const [toDelete, setToDelete] = useState<BlogPost | null>(null)

  const { data, isLoading, isError, error, refetch, isFetching } = useBlogPosts({ page, length, search, status })
  const deletePost = useDeleteBlogPost()
  const restorePost = useRestoreBlogPost()
  const purgePost = usePermanentDeleteBlogPost()
  const [toPurge, setToPurge] = useState<BlogPost | null>(null)
  const inTrash = status === 'trash'

  const applySearch = useDebouncedCallback((value: string) => {
    setSearch(value.trim())
    setPage(1)
  }, 400)

  const confirmDelete = () => {
    if (!toDelete) return
    deletePost.mutate(toDelete._id, {
      onSuccess: () => {
        toast.success('Post moved to the trash')
        setToDelete(null)
      },
      onError: (e) => toast.error(e.message),
    })
  }

  const restore = (post: BlogPost) =>
    restorePost.mutate(post._id, {
      onSuccess: () => toast.success('Post restored'),
      onError: (e) => toast.error(e.message),
    })

  const confirmPurge = () => {
    if (!toPurge) return
    purgePost.mutate(toPurge._id, {
      onSuccess: (bodyRemoved) => {
        toast.success(bodyRemoved ? 'Post deleted for good' : 'Post deleted; its body file is still in the media library')
        setToPurge(null)
      },
      onError: (e) => toast.error(e.message),
    })
  }

  const posts = data?.result ?? []

  return (
    <div className="space-y-4">
      <PageHeader
        title="Blog posts"
        description="Write posts for your storefront's blog."
        onAddClick={() => navigate('/blog/new')}
        addButtonText="New post"
      />

      <Card>
        <CardHeader>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <Tabs
              value={status}
              onValueChange={(v) => {
                setStatus(v as StatusTab)
                setPage(1)
              }}
            >
              <TabsList>
                {TABS.map((t) => (
                  <TabsTrigger key={t.value} value={t.value}>
                    {t.label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
            <div className="flex items-center gap-2">
              <div className="relative w-full sm:w-64">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  aria-label="Search posts by title"
                  placeholder="Search by title…"
                  className="pl-8"
                  value={searchInput}
                  onChange={(e) => {
                    setSearchInput(e.target.value)
                    applySearch(e.target.value)
                  }}
                />
              </div>
              <Button variant="outline" size="icon" aria-label="Refresh" onClick={() => refetch()}>
                <RefreshCw className={isFetching ? 'size-4 animate-spin' : 'size-4'} />
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              {Array.from({ length: 4 }, (_, i) => (
                <Skeleton key={i} className="h-14 w-full" />
              ))}
            </div>
          ) : isError ? (
            <Alert variant="destructive">
              <AlertCircle className="size-4" />
              <AlertTitle>Could not load blog posts</AlertTitle>
              <AlertDescription className="flex flex-wrap items-center gap-3">
                <span>{error.message}</span>
                <Button variant="outline" size="sm" onClick={() => refetch()}>
                  Try again
                </Button>
              </AlertDescription>
            </Alert>
          ) : posts.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-12 text-center">
              <FileText className="size-10 text-faint" aria-hidden="true" />
              <p className="text-heading-md text-ink">
                {search ? 'No posts found' : inTrash ? 'The trash is empty' : 'Write your first blog post'}
              </p>
              <p className="max-w-sm text-sm text-muted-foreground">
                {search
                  ? `No posts match "${search}". Try a different title.`
                  : inTrash
                    ? 'Posts you move to the trash show up here.'
                    : 'Share news, guides and stories with your customers on your storefront.'}
              </p>
              {!search && !inTrash && (
                <Button className="mt-2" onClick={() => navigate('/blog/new')}>
                  New post
                </Button>
              )}
            </div>
          ) : (
            <ul className="-mx-4 divide-y divide-border-subtle border-y border-border-subtle">
              {posts.map((post) => (
                <li key={post._id} className="flex items-center gap-3 px-4 py-2 transition-colors hover:bg-subdued">
                  <div className="size-10 shrink-0 overflow-hidden rounded-md border border-border-subtle bg-subdued">
                    {post.banner?.path && (
                      <img src={getFileUrl(post.banner.path)} alt="" className="size-full object-cover" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        className="min-w-0 truncate text-left text-sm font-medium text-ink hover:underline"
                        onClick={() => navigate(`/blog/${post._id}`)}
                      >
                        {post.title}
                      </button>
                      <span className="shrink-0">
                        <BlogStatusBadge status={post.status} publishedAt={post.published_at} />
                      </span>
                    </div>
                    <p className="truncate text-xs text-muted-foreground">
                      /blog/{post.slug} · {formatDate(post.published_at || post.created_at)}{post.category ? ` · ${post.category}` : ''}
                      {post.read_duration ? ` · ${post.read_duration} min read` : ''}
                    </p>
                  </div>
                  {inTrash ? (
                    <div className="flex shrink-0 items-center gap-1">
                      <Button variant="ghost" size="sm" disabled={restorePost.isPending} onClick={() => restore(post)}>
                        <ArchiveRestore className="size-4" />
                        Restore
                      </Button>
                      <Button variant="ghost" size="sm" className="text-error" onClick={() => setToPurge(post)}>
                        <Trash2 className="size-4" />
                        Delete forever
                      </Button>
                    </div>
                  ) : (
                    <div className="flex shrink-0 items-center gap-1">
                      <Button variant="ghost" size="icon" aria-label="Preview" onClick={() => navigate(`/blog/${post._id}/preview`)}>
                        <Eye className="size-4" />
                      </Button>
                      <Button variant="ghost" size="icon" aria-label="Edit" onClick={() => navigate(`/blog/${post._id}`)}>
                        <Pencil className="size-4" />
                      </Button>
                      <Button variant="ghost" size="icon" aria-label="Delete" onClick={() => setToDelete(post)}>
                        <Trash2 className="size-4 text-error" />
                      </Button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}

          {data && !isError && (
            <div className="flex flex-col gap-2 pt-4">
              <Pagination.Buttons
                currentPage={page}
                totalItems={data.count}
                pageSize={length}
                onPageChange={setPage}
              />
              <Pagination.PageSizeDropdown
                pageSize={length}
                onPageSizeChange={(size) => {
                  setLength(size)
                  setPage(1)
                }}
              />
            </div>
          )}
        </CardContent>
      </Card>

      <ConfirmDialog
        open={Boolean(toDelete)}
        onOpenChange={(open) => !open && setToDelete(null)}
        title="Move this post to the trash?"
        description={`"${toDelete?.title ?? ''}" will be removed from your blog. You can restore it from the Trash tab.`}
        confirmText="Move to trash"
        variant="destructive"
        loading={deletePost.isPending}
        onConfirm={confirmDelete}
      />

      <ConfirmDialog
        open={Boolean(toPurge)}
        onOpenChange={(open) => !open && setToPurge(null)}
        title="Delete this post for good?"
        description={`"${toPurge?.title ?? ''}" and its text will be deleted permanently. This can't be undone.`}
        confirmText="Delete forever"
        variant="destructive"
        loading={purgePost.isPending}
        onConfirm={confirmPurge}
      />
    </div>
  )
}
